import type * as APITypes from '@viamrobotics/web-client/app/v1/app';
import { Timestamp } from '@viamrobotics/web-client/google/protobuf/timestamp';
import type { User } from '@viamrobotics/web-client/user/v1/user';

import { getMostRecentOrgID } from './get-most-recent-org';

/** Data from an RPC API call to ListOrganizations. */
export interface ListOrganizationsQuery {
  data: APITypes.Organization[] | undefined;
}

/** Organizations state. */
export interface Organizations {
  current: APITypes.Organization | undefined;
  all: APITypes.Organization[];
  recent?: APITypes.Organization[] | undefined;
  byID: Record<string, APITypes.Organization>;
  other: APITypes.Organization[];
}

/** Given a selected org and data from the API, derive organizations state. */
export const getCurrentOrg = ([selectedOrgID, orgsQuery, user]: [
  selectedOrgID: string | undefined,
  orgsQuery: ListOrganizationsQuery,
  user: User | undefined,
]): Organizations => {
  const all = orgsQuery.data ?? [];
  const currentOrgID = selectedOrgID ?? getMostRecentOrgID(user?.orgLastAccess);
  const current = all.find(({ id }) => id === currentOrgID) ?? all[0];
  const recent =
    all.length >= 16
      ? getRecentOrgs(selectedOrgID, all, user?.orgLastAccess)
      : undefined;
  const other = all.filter(({ id }) => id !== current?.id);

  const byID = Object.fromEntries(all.map((org) => [org.id, org]));

  return { current, all, recent, byID, other };
};

const getRecentOrgs = (
  currentOrgID: string | undefined,
  allOrgs: APITypes.Organization[],
  orgLastAccess: Record<string, Timestamp> | undefined
): APITypes.Organization[] | undefined => {
  // remove current org from recents, if it is defined
  let recent = currentOrgID
    ? allOrgs.filter(({ id }) => id !== currentOrgID)
    : allOrgs;

  if (orgLastAccess) {
    // filter out any orgs that don't have a last_access timestamp
    recent = recent.filter((org) => orgLastAccess[org.id] !== undefined);

    // sort remaining orgs by their timestamp
    recent = recent.sort((orgA, orgB) => {
      // orgLastAccess[<key>] should never be undefined because of the filter above,
      // but typescript requires a fallback value
      const orgATime =
        orgLastAccess[orgA.id]?.seconds ??
        Timestamp.fromDate(new Date(0)).seconds;
      const orgBTime =
        orgLastAccess[orgB.id]?.seconds ??
        Timestamp.fromDate(new Date(0)).seconds;
      if (orgATime < orgBTime) return 1;
      if (orgATime > orgBTime) return -1;
      return 0;
    });
    // only return the 15 most recent organizations
    return recent.slice(0, 15);
  }
  return undefined;
};

/** Given a users known organizations, get the ID of the current org. */
export const getCurrentOrgID = ([orgs]: [orgs: Organizations]):
  | string
  | undefined => {
  return orgs.current?.id;
};

/**
 * Get the next org ID to use after the current one.
 *
 * Useful in contexts where we need to switch away from the current org,
 * e.g. delete organization or remove self from organization.
 */
export const getNextOrgID = (
  orgs: Organizations,
  removedOrgID: string
): string | undefined => {
  return orgs.current?.id === removedOrgID ? orgs.other[0]?.id : undefined;
};

export const getNextOrgIDAfterMemberRemoval = (
  orgs: Organizations,
  appUserID: string | undefined,
  removedOrgID: string,
  removedUserID: string
): string | undefined => {
  return appUserID === removedUserID
    ? getNextOrgID(orgs, removedOrgID)
    : undefined;
};
